import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  ArrowLeft, 
  FileText,
  Download,
  Search,
  Filter,
  Loader2,
  Calendar,
  Package,
  Truck,
  Receipt,
  Tag,
  RefreshCw,
  CheckCircle,
  Eye,
  Printer,
  Clock
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const DocumentsHubPage = () => {
  const navigate = useNavigate();
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState('30'); // days
  const [selectedDocs, setSelectedDocs] = useState(new Set());
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    fetchDocuments();
  }, [typeFilter, dateFilter]);

  const fetchDocuments = async () => {
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      params.append('days', dateFilter);
      if (typeFilter !== 'all') params.append('type', typeFilter);
      
      const response = await axios.get(
        `${API_BASE_URL}/couriers/documents?${params}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setDocuments(response.data.documents || []);
    } catch (error) {
      console.error('Error fetching documents:', error);
      // Mock data for demo if API not available
      setDocuments([
        {
          id: '1',
          type: 'shipping_label',
          order_number: 'ORD-2024-001',
          awb_number: 'DEL123456789',
          courier_type: 'delhivery',
          created_at: new Date().toISOString(),
          url: '#'
        },
        {
          id: '2',
          type: 'packing_slip',
          order_number: 'ORD-2024-002',
          created_at: new Date(Date.now() - 86400000).toISOString(),
          url: '#'
        },
        {
          id: '3',
          type: 'invoice',
          order_number: 'ORD-2024-003',
          invoice_number: 'INV-001',
          created_at: new Date(Date.now() - 172800000).toISOString(),
          url: '#'
        }
      ]);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleRefresh = () => {
    setRefreshing(true);
    fetchDocuments();
  };

  const toggleSelectDoc = (docId) => {
    setSelectedDocs(prev => {
      const newSet = new Set(prev);
      if (newSet.has(docId)) {
        newSet.delete(docId);
      } else {
        newSet.add(docId);
      }
      return newSet;
    });
  };

  const selectAll = () => {
    if (selectedDocs.size === filteredDocuments.length) {
      setSelectedDocs(new Set());
    } else {
      setSelectedDocs(new Set(filteredDocuments.map(d => d.id)));
    }
  };

  const handleBulkDownload = async () => {
    if (selectedDocs.size === 0) {
      toast.error('Please select documents to download');
      return;
    }

    setDownloading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_BASE_URL}/couriers/documents/bulk-download`,
        { document_ids: [...selectedDocs] },
        { 
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );
      
      const blob = new Blob([response.data], { type: 'application/zip' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `documents-${new Date().toISOString().slice(0, 10)}.zip`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      toast.success(`Downloaded ${selectedDocs.size} documents`);
      setSelectedDocs(new Set());
    } catch (error) {
      toast.error('Failed to download documents');
    } finally {
      setDownloading(false);
    }
  };

  const handleDownloadSingle = async (doc) => {
    // For shipping labels with courier type, always use the backend API
    if (doc.type === 'shipping_label' && doc.awb_number) {
      try {
        const token = localStorage.getItem('token');
        
        // First try to find the shipment by AWB
        const shipmentRes = await axios.get(
          `${API_BASE_URL}/couriers/shipments?awb=${doc.awb_number}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        const shipment = shipmentRes.data.shipments?.[0];
        if (shipment?.id) {
          const labelRes = await axios.get(
            `${API_BASE_URL}/v1/couriers/shipments/${shipment.id}/label`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          
          if (labelRes.data?.download_url || labelRes.data?.has_content) {
            // Use the direct download endpoint
            window.open(`${API_BASE_URL}/couriers/shipments/${shipment.id}/label/download`, '_blank');
            toast.success('Downloading label...');
            return;
          } else if (labelRes.data?.label_url) {
            window.open(labelRes.data.label_url, '_blank');
            if (labelRes.data?.portal_login_required) {
              toast.info(labelRes.data.message || 'Please log into the courier portal to download the label');
            } else {
              toast.success('Label opened in new tab');
            }
            return;
          } else {
            toast.error(labelRes.data?.error || 'Label not available');
            return;
          }
        } else {
          toast.error('Shipment not found');
          return;
        }
      } catch (err) {
        console.error('Label fetch error:', err);
        toast.error('Failed to download label');
        return;
      }
    }
    
    // Check if document has a valid URL (not for DTDC labels)
    if (doc.url && doc.url !== '#' && doc.url.startsWith('http') && !doc.url.includes('dtdc.in/tracking')) {
      window.open(doc.url, '_blank');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API_BASE_URL}/couriers/documents/${doc.id}/download`,
        { 
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      // If the response contains a URL, redirect to it
      if (response.data?.url) {
        window.open(response.data.url, '_blank');
        return;
      }
      
      // Handle redirect response
      if (response.request?.responseURL) {
        window.open(response.request.responseURL, '_blank');
        return;
      }
      
      toast.error('Document URL not available. The document may still be generating.');
    } catch (error) {
      console.error('Download error:', error);
      if (error.response?.status === 404) {
        toast.error('Document not found or no longer available');
      } else {
        toast.error('Failed to download document');
      }
    }
  };

  const getTypeIcon = (type) => {
    const icons = {
      shipping_label: Tag,
      packing_slip: Package,
      invoice: Receipt,
      picking_summary: FileText
    };
    return icons[type] || FileText;
  };

  const getTypeColor = (type) => {
    const colors = {
      shipping_label: 'bg-blue-500/20 text-blue-400',
      packing_slip: 'bg-purple-500/20 text-purple-400',
      invoice: 'bg-green-500/20 text-green-400',
      picking_summary: 'bg-amber-500/20 text-amber-400'
    };
    return colors[type] || 'bg-gray-500/20 text-gray-400';
  };

  const getTypeLabel = (type) => {
    const labels = {
      shipping_label: 'Shipping Label',
      packing_slip: 'Packing Slip',
      invoice: 'Invoice',
      picking_summary: 'Picking Summary'
    };
    return labels[type] || type;
  };

  const filteredDocuments = documents.filter(doc => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      doc.order_number?.toLowerCase().includes(query) ||
      doc.awb_number?.toLowerCase().includes(query) ||
      doc.invoice_number?.toLowerCase().includes(query)
    );
  });

  const groupedByDate = filteredDocuments.reduce((acc, doc) => {
    const date = new Date(doc.created_at).toLocaleDateString();
    if (!acc[date]) acc[date] = [];
    acc[date].push(doc);
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="h-8 w-8 animate-spin text-wt-gold" />
      </div>
    );
  }

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="outline"
            size="icon"
            onClick={() => navigate('/orders')}
            className="border-wt-border"
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div className="flex items-center gap-3">
            <div className="p-3 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600">
              <FileText className="h-8 w-8 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-wt-text-primary">Documents Hub</h1>
              <p className="text-wt-text-muted">Labels, packing slips & invoices (last {dateFilter} days)</p>
            </div>
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          {selectedDocs.size > 0 && (
            <Button
              onClick={handleBulkDownload}
              disabled={downloading}
              className="bg-wt-gold hover:bg-wt-gold-dark text-black"
            >
              {downloading ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <Download className="h-4 w-4 mr-2" />
              )}
              Download ({selectedDocs.size})
            </Button>
          )}
          <Button
            onClick={handleRefresh}
            disabled={refreshing}
            variant="outline"
            className="border-wt-border"
          >
            <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-wt-text-muted" />
          <Input
            placeholder="Search by order #, AWB, or invoice #..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 bg-wt-bg border-wt-border text-wt-text-primary"
          />
        </div>
        <Select value={typeFilter} onValueChange={setTypeFilter}>
          <SelectTrigger className="w-40 bg-wt-bg border-wt-border text-wt-text-primary">
            <SelectValue placeholder="Type" />
          </SelectTrigger>
          <SelectContent className="bg-wt-surface border-wt-border">
            <SelectItem value="all">All Types</SelectItem>
            <SelectItem value="shipping_label">Labels</SelectItem>
            <SelectItem value="packing_slip">Packing Slips</SelectItem>
            <SelectItem value="invoice">Invoices</SelectItem>
          </SelectContent>
        </Select>
        <Select value={dateFilter} onValueChange={setDateFilter}>
          <SelectTrigger className="w-36 bg-wt-bg border-wt-border text-wt-text-primary">
            <Clock className="h-4 w-4 mr-2" />
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-wt-surface border-wt-border">
            <SelectItem value="7">Last 7 days</SelectItem>
            <SelectItem value="14">Last 14 days</SelectItem>
            <SelectItem value="30">Last 30 days</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Documents List */}
      {filteredDocuments.length === 0 ? (
        <div className="bg-wt-surface border border-wt-border rounded-xl p-12 text-center">
          <FileText className="h-16 w-16 mx-auto mb-4 text-wt-text-muted opacity-50" />
          <h3 className="text-xl font-semibold text-wt-text-primary mb-2">No Documents Found</h3>
          <p className="text-wt-text-muted">
            {searchQuery 
              ? 'No documents match your search'
              : `No documents generated in the last ${dateFilter} days`}
          </p>
        </div>
      ) : (
        <div className="space-y-6">
          {/* Select All */}
          <div className="flex items-center gap-3 px-4 py-2 bg-wt-surface border border-wt-border rounded-lg">
            <Checkbox
              id="select-all-docs"
              checked={selectedDocs.size === filteredDocuments.length && filteredDocuments.length > 0}
              onCheckedChange={selectAll}
              className="border-wt-border"
            />
            <label htmlFor="select-all-docs" className="text-sm text-wt-text-secondary cursor-pointer">
              Select all {filteredDocuments.length} documents
            </label>
          </div>

          {/* Grouped by Date */}
          {Object.entries(groupedByDate).map(([date, docs]) => (
            <div key={date}>
              <div className="flex items-center gap-2 mb-3">
                <Calendar className="h-4 w-4 text-wt-text-muted" />
                <span className="text-sm font-medium text-wt-text-muted">{date}</span>
                <span className="text-xs text-wt-text-muted">({docs.length} documents)</span>
              </div>
              
              <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
                {docs.map((doc, idx) => {
                  const TypeIcon = getTypeIcon(doc.type);
                  
                  return (
                    <motion.div
                      key={doc.id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: idx * 0.03 }}
                      className={`bg-wt-paper border rounded-xl p-4 hover:border-wt-gold/30 transition-colors ${
                        selectedDocs.has(doc.id) ? 'border-wt-gold' : 'border-wt-border'
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        <Checkbox
                          checked={selectedDocs.has(doc.id)}
                          onCheckedChange={() => toggleSelectDoc(doc.id)}
                          className="mt-1 border-wt-border"
                        />
                        
                        <div className={`p-2 rounded-lg ${getTypeColor(doc.type)}`}>
                          <TypeIcon className="h-5 w-5" />
                        </div>
                        
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-wt-text-primary truncate">
                            {doc.order_number}
                          </div>
                          <Badge className={`mt-1 ${getTypeColor(doc.type)} border-none text-xs`}>
                            {getTypeLabel(doc.type)}
                          </Badge>
                          
                          {doc.awb_number && (
                            <div className="text-xs text-wt-text-muted mt-2">
                              AWB: {doc.awb_number}
                            </div>
                          )}
                          {doc.invoice_number && (
                            <div className="text-xs text-wt-text-muted mt-2">
                              Invoice: {doc.invoice_number}
                            </div>
                          )}
                          
                          <div className="text-xs text-wt-text-muted mt-1">
                            {new Date(doc.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                          </div>
                        </div>
                        
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDownloadSingle(doc)}
                          className="text-wt-text-muted hover:text-wt-gold"
                        >
                          <Download className="h-4 w-4" />
                        </Button>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default DocumentsHubPage;
